import { useRef, useState } from 'react'
import { Loader2, Plus } from 'lucide-react'
import { useContractFiles, useUploadFile } from '../files/hooks'
import { useDocumentTypes } from '@/features/admin/document-types/hooks'
import { useContract } from '../hooks'
import type { FileAttachment } from '../files/types'
import { FileLightbox } from './FileLightbox'
import { GalleryEmptyDropzone } from './GalleryEmptyDropzone'
import { GalleryThumbnail } from './GalleryThumbnail'
import { useFieldDensity } from './field-renderer/FieldDensity'

type Props = {
  contractId: number
  documentTypeId: number
  /** Aktualna wartość - lista fileIds (z extras). */
  value: number[]
  onChange: (fileIds: number[]) => void
  disabled?: boolean
}

/**
 * Galeria zdjęć w formularzu. Wartością pola jest tablica fileIds. Każdy upload
 * tworzy plik w files.file_attachment z documentTypeId pobranym z definicji
 * pola; pliki pojawiają się też w zakładce Pliki umowy.
 *
 * UX: grid thumbnaili, "+" tile na końcu otwiera multi-file picker, klik na
 * thumbnailu otwiera fullscreen lightbox, X usuwa z listy (plik fizyczny
 * zostaje - kasuje się go w FilesTab).
 */
export function GalleryFieldInput({ contractId, documentTypeId, value, onChange, disabled }: Props) {
  const density = useFieldDensity()
  const contractQ = useContract(contractId)
  const configurationCode = contractQ.data?.configurationCode
  const docTypesQ = useDocumentTypes(configurationCode, true)
  const filesQ = useContractFiles(contractId)
  const upload = useUploadFile(contractId)
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [lightbox, setLightbox] = useState<number | null>(null)

  const docType = docTypesQ.data?.find(d => d.id === documentTypeId)
  const accepted = docType?.acceptedExtensions ?? '.jpg,.jpeg,.png,.webp,.gif'

  const validateExt = (filename: string): string | null => {
    const allowed = accepted.split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
    if (allowed.length === 0) return null
    const lower = filename.toLowerCase()
    if (allowed.some(ext => lower.endsWith(ext))) return null
    return `${filename}: dozwolone tylko ${accepted}`
  }

  const handleFiles = async (files: FileList) => {
    setError(null)
    const arr = Array.from(files)
    for (const f of arr) {
      const err = validateExt(f.name)
      if (err) {
        setError(err)
        return
      }
    }
    // Sekwencyjnie - prostsze + nie obciąża backendu burst-em.
    const newIds: number[] = []
    for (const f of arr) {
      try {
        const created = await new Promise<FileAttachment>((resolve, reject) => {
          upload.mutate(
            { file: f, documentTypeId },
            {
              onSuccess: resolve,
              onError: reject,
            },
          )
        })
        newIds.push(created.id)
      } catch (e: unknown) {
        const ax = e as { response?: { data?: { detail?: string } } }
        setError(ax.response?.data?.detail ?? `Błąd uploadu ${f.name}`)
        break
      }
    }
    if (newIds.length > 0) onChange([...value, ...newIds])
  }

  const removeAt = (fileId: number) => {
    onChange(value.filter(id => id !== fileId))
  }

  const onPick = () => inputRef.current?.click()

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={inputRef}
        type="file"
        accept={accepted}
        multiple
        className="hidden"
        disabled={disabled || upload.isPending}
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) handleFiles(e.target.files)
          e.target.value = ''
        }}
      />

      {value.length === 0 && !disabled ? (
        <GalleryEmptyDropzone onPick={onPick} accepted={accepted} pending={upload.isPending} />
      ) : (
        <div className={density === 'compact'
          ? 'grid grid-cols-3 gap-1.5'
          : 'grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2'
        }>
          {value.map((fid, idx) => {
            const meta = filesQ.data?.find(f => f.id === fid)
            return (
              <GalleryThumbnail
                key={fid}
                contractId={contractId}
                fileId={fid}
                meta={meta}
                onClick={() => setLightbox(idx)}
                onRemove={disabled ? undefined : () => removeAt(fid)}
              />
            )
          })}
          {!disabled && (
            <button
              type="button"
              onClick={onPick}
              disabled={upload.isPending}
              className={[
                'aspect-square rounded-md border-2 border-dashed border-slate-300 hover:border-sky-400 hover:bg-sky-50/50',
                'flex flex-col items-center justify-center gap-1 text-slate-500 hover:text-sky-700 transition-colors disabled:opacity-60',
              ].join(' ')}
            >
              {upload.isPending ? (
                <Loader2 size={density === 'compact' ? 14 : 20} className="animate-spin" />
              ) : (
                <>
                  <Plus size={density === 'compact' ? 14 : 20} />
                  <span className="text-[10px] font-medium">Dodaj</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {error && <span className="text-xs text-rose-600">{error}</span>}

      {lightbox !== null && (
        <FileLightbox
          contractId={contractId}
          fileIds={value}
          startIndex={lightbox}
          filesMeta={filesQ.data ?? []}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  )
}

